import { SettingsHelp, SettingsSaveBar } from "./ProposalSettingsUI";
/**
 * DefaultPricingTab - Configure price per square and tier assignments for manufacturer products
 */

import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Input,
  Sheet,
  CircularProgress,
  Alert,
  Chip,
  Checkbox,
  AccordionSummary,
  AccordionDetails,
  AccordionGroup,
  Select,
  Option,
  Avatar,
  FormControl,
  FormLabel,
} from "@mui/joy";
import {
  MagnifyingGlass,
  CaretDown as ExpandMore,
  CurrencyDollar as AttachMoney,
  Package as Inventory,
} from "@phosphor-icons/react";
import { proposalsApi } from "../../lib/proposalsApi";
import { useIsMobile } from "../../hooks/useIsMobile";
import { TabbableAccordion } from "../common/TabbableAccordion";
import { useTierLabels } from "../../hooks/useTierLabels";
import { TIER_TYPES } from "../../types/proposals";
import type { TierLabelMap, TierType } from "../../types/proposals";

interface PricingState {
  [productId: number]: {
    price_per_square: string;
    minimum_price_per_square: string;
    tier_type: TierType | null;
    existingId: number | null;
    hasChanges: boolean;
  };
}

const TIER_COLORS: Record<TierType, "warning" | "primary" | "success"> = {
  good: "warning",
  better: "primary",
  best: "success",
};

type TierOption = {
  value: TierType | null;
  label: string;
  color: "warning" | "primary" | "success" | "neutral";
};

// Tier names are org-configurable, so the dropdown is built from the org's
// labels rather than a hardcoded Good/Better/Best list
const buildTierOptions = (tierLabels: TierLabelMap): TierOption[] => [
  { value: null, label: "None", color: "neutral" },
  ...TIER_TYPES.map((tierType) => ({
    value: tierType,
    label: tierLabels[tierType],
    color: TIER_COLORS[tierType],
  })),
];

export default function DefaultPricingTab({
  onDirtyChange,
}: {
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [pricingState, setPricingState] = useState<PricingState>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [search, setSearch] = useState("");
  const [unpricedOnly, setUnpricedOnly] = useState(false);
  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
    return () => onDirtyChange?.(false);
  }, [hasUnsavedChanges, onDirtyChange]);
  const { labels: tierLabels } = useTierLabels();
  const tierOptions = useMemo(() => buildTierOptions(tierLabels), [tierLabels]);

  // Fetch manufacturers with products and pricing
  const {
    data: manufacturersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["product-pricing-by-manufacturer"],
    queryFn: () => proposalsApi.getProductPricingByManufacturer(),
  });

  // Initialize pricing state when data loads
  useEffect(() => {
    if (manufacturersData && !hasUnsavedChanges) {
      const initialState: PricingState = {};
      manufacturersData.forEach((mfr) => {
        mfr.products.forEach((product) => {
          initialState[product.id] = {
            price_per_square: product.pricing?.price_per_square || "",
            minimum_price_per_square:
              product.pricing?.minimum_price_per_square || "",
            tier_type: product.pricing?.tier_type || null,
            existingId: product.pricing?.id || null,
            hasChanges: false,
          };
        });
      });
      setPricingState(initialState);
      setHasUnsavedChanges(false);
    }
  }, [manufacturersData, hasUnsavedChanges]);

  // Bulk save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      // Validate min price <= price
      for (const [productId, state] of Object.entries(pricingState)) {
        if (state.minimum_price_per_square && state.price_per_square) {
          const minPrice = parseFloat(state.minimum_price_per_square);
          const price = parseFloat(state.price_per_square);
          if (!isNaN(minPrice) && !isNaN(price) && minPrice > price) {
            const product = manufacturersData
              ?.flatMap((row) => row.products)
              .find((row) => String(row.id) === productId);
            throw new Error(
              `${product?.name || "Product"}: minimum price cannot be greater than the default price per square.`,
            );
          }
        }
      }

      const entries = Object.entries(pricingState);
      const hasPrice = (value: string) =>
        value.trim() !== "" && !isNaN(parseFloat(value));

      // Collect all items that have a price (including $0, which the backend accepts)
      const pricingItems = entries
        .filter(([_, state]) => hasPrice(state.price_per_square))
        .map(([productId, state]) => ({
          product: parseInt(productId, 10),
          price_per_square: state.price_per_square,
          minimum_price_per_square: state.minimum_price_per_square || null,
          tier_type: state.tier_type,
        }));

      // A cleared price on a saved product removes its pricing row (price is
      // required on the model, so there is no "priced with no price" state)
      const removedIds = entries
        .filter(
          ([_, state]) =>
            state.hasChanges &&
            state.existingId !== null &&
            !hasPrice(state.price_per_square),
        )
        .map(([_, state]) => state.existingId as number);

      if (pricingItems.length === 0 && removedIds.length === 0) {
        throw new Error("No pricing data to save");
      }

      for (const pricingId of removedIds) {
        await proposalsApi.deleteProductPricing(pricingId);
      }

      if (pricingItems.length === 0) {
        return {
          status: "success",
          created: 0,
          updated: 0,
          errors: [],
          removed: removedIds.length,
        };
      }

      const result = await proposalsApi.bulkUpdateProductPricing(pricingItems);
      return { ...result, removed: removedIds.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["product-pricing-by-manufacturer"],
      });
      setHasUnsavedChanges(false);
      const removedSuffix = data.removed > 0 ? `, ${data.removed} removed` : "";
      if (data.errors.length > 0) {
        toast.error(
          `Saved ${data.created + data.updated} items, but ${data.errors.length} failed`,
        );
      } else {
        toast.success(
          `Pricing saved: ${data.created} created, ${data.updated} updated${removedSuffix}`,
        );
      }
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to save pricing");
    },
  });

  // Handle price change
  const handlePriceChange = (productId: number, value: string) => {
    setPricingState((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        price_per_square: value,
        hasChanges: true,
      },
    }));
    setHasUnsavedChanges(true);
  };

  // Handle tier change
  const handleTierChange = (productId: number, value: TierType | null) => {
    setPricingState((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        tier_type: value,
        hasChanges: true,
      },
    }));
    setHasUnsavedChanges(true);
  };

  // Handle minimum price change
  const handleMinPriceChange = (productId: number, value: string) => {
    setPricingState((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        minimum_price_per_square: value,
        hasChanges: true,
      },
    }));
    setHasUnsavedChanges(true);
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalProducts = Object.keys(pricingState).length;
    const productsWithPricing = Object.values(pricingState).filter(
      (s) =>
        s.price_per_square.trim() !== "" && Number(s.price_per_square) >= 0,
    ).length;
    const productsWithTiers = Object.values(pricingState).filter(
      (s) => s.tier_type,
    ).length;
    return { totalProducts, productsWithPricing, productsWithTiers };
  }, [pricingState]);

  const visibleManufacturers = (manufacturersData || [])
    .map((manufacturer) => ({
      ...manufacturer,
      products: manufacturer.products.filter(
        (product) =>
          `${manufacturer.name} ${product.name} ${product.sku || ""}`
            .toLowerCase()
            .includes(search.trim().toLowerCase()) &&
          (!unpricedOnly || !pricingState[product.id]?.price_per_square.trim()),
      ),
    }))
    .filter((manufacturer) => manufacturer.products.length);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Failed to load product data. Please try again.
      </Alert>
    );
  }

  if (!manufacturersData || manufacturersData.length === 0) {
    return (
      <Card variant="soft" sx={{ m: 2, textAlign: "center" }}>
        <CardContent>
          <Inventory
            size={48}
            color="var(--joy-palette-text-tertiary)"
            style={{ marginBottom: 16 }}
          />
          <Typography level="h4">No Manufacturer Products</Typography>
          <Typography level="body-md" sx={{ color: "text.secondary" }}>
            No manufacturer products have been added to the system yet. Contact
            your administrator to add products.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography level="h4">Roofing product prices</Typography>
          <Typography level="body-sm" textColor="text.secondary">
            Set the starting price per square and package tier for each product.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Chip size="sm" color="success">
            {stats.productsWithPricing} of {stats.totalProducts} prices set
          </Chip>
          <Chip size="sm">{stats.productsWithTiers} assigned to a tier</Chip>
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          gap={2}
          alignItems={{ sm: "center" }}
        >
          <Input
            startDecorator={<MagnifyingGlass />}
            placeholder="Search products or manufacturers…"
            slotProps={{
              input: { "aria-label": "Search products or manufacturers" },
            }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ flex: 1 }}
          />
          <Checkbox
            label="Missing prices only"
            checked={unpricedOnly}
            onChange={(event) => setUnpricedOnly(event.target.checked)}
          />
          <SettingsHelp title="Prices and minimums">
            One roofing square is 100 square feet. The default price starts a
            proposal; the optional minimum limits salesperson discounts. Org
            Admins can override the minimum on a proposal.
          </SettingsHelp>
        </Stack>
      </Stack>
      {visibleManufacturers.length === 0 && (
        <Card variant="soft">
          <Typography>No products match these filters.</Typography>
          <Button
            variant="plain"
            onClick={() => {
              setSearch("");
              setUnpricedOnly(false);
            }}
          >
            Clear filters
          </Button>
        </Card>
      )}
      {/* Manufacturer Accordions */}
      <AccordionGroup variant="outlined" sx={{ borderRadius: "sm" }}>
        {visibleManufacturers.map((manufacturer) => (
          <TabbableAccordion
            defaultExpanded={visibleManufacturers.length <= 3}
            key={`${manufacturer.id}-${Boolean(search || unpricedOnly)}`}
          >
            <AccordionSummary indicator={<ExpandMore />}>
              <Stack direction="row" alignItems="center" spacing={2}>
                {manufacturer.logo ? (
                  <Avatar
                    src={manufacturer.logo}
                    alt={manufacturer.name}
                    size="sm"
                  />
                ) : (
                  <Avatar size="sm">{manufacturer.name.charAt(0)}</Avatar>
                )}
                <Typography level="title-md">{manufacturer.name}</Typography>
                <Chip size="sm" variant="soft">
                  {manufacturer.products.length} products
                </Chip>
                {/* Show count of priced products */}
                {(() => {
                  const pricedCount = manufacturer.products.filter(
                    (p) =>
                      pricingState[p.id]?.price_per_square.trim() !== "" &&
                      Number(pricingState[p.id]?.price_per_square) >= 0,
                  ).length;
                  if (pricedCount > 0) {
                    return (
                      <Chip size="sm" variant="soft" color="success">
                        {pricedCount} priced
                      </Chip>
                    );
                  }
                  return null;
                })()}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {isMobile ? (
                <Stack spacing={1.5}>
                  {manufacturer.products.map((product) => {
                    const state = pricingState[product.id] || {
                      price_per_square: "",
                      minimum_price_per_square: "",
                      tier_type: null,
                      existingId: null,
                      hasChanges: false,
                    };
                    return (
                      <Card
                        key={product.id}
                        variant="outlined"
                        sx={{
                          p: 2,
                          gap: 1.5,
                          bgcolor: state.hasChanges
                            ? "warning.softBg"
                            : undefined,
                        }}
                      >
                        <Box>
                          <Typography level="title-sm">
                            {product.name}
                          </Typography>
                          {product.description && (
                            <Typography
                              level="body-xs"
                              sx={{ color: "text.tertiary" }}
                            >
                              {product.description.length > 80
                                ? `${product.description.substring(0, 80)}...`
                                : product.description}
                            </Typography>
                          )}
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{ mt: 0.5 }}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Typography
                              level="body-xs"
                              sx={{
                                color: "text.secondary",
                                fontFamily: "monospace",
                              }}
                            >
                              SKU: {product.sku || "-"}
                            </Typography>
                            <Typography
                              level="body-xs"
                              sx={{ color: "text.secondary" }}
                            >
                              Warranty:{" "}
                              {product.warranty_years
                                ? `${product.warranty_years} yr`
                                : "-"}
                            </Typography>
                          </Stack>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <FormControl size="sm" sx={{ flex: 1 }}>
                            <FormLabel>Price/Square</FormLabel>
                            <Input
                              type="number"
                              placeholder="0.00"
                              value={state.price_per_square}
                              onChange={(e) =>
                                handlePriceChange(product.id, e.target.value)
                              }
                              startDecorator={
                                <AttachMoney
                                  size={16}
                                  color="var(--joy-palette-text-tertiary)"
                                />
                              }
                              endDecorator={
                                <Typography
                                  level="body-xs"
                                  sx={{ color: "text.tertiary" }}
                                >
                                  /sq
                                </Typography>
                              }
                            />
                          </FormControl>
                          <FormControl size="sm" sx={{ flex: 1 }}>
                            <FormLabel>Min Price/Sq</FormLabel>
                            <Input
                              type="number"
                              placeholder="—"
                              value={state.minimum_price_per_square}
                              onChange={(e) =>
                                handleMinPriceChange(product.id, e.target.value)
                              }
                              startDecorator={
                                <AttachMoney
                                  size={16}
                                  color="var(--joy-palette-text-tertiary)"
                                />
                              }
                              endDecorator={
                                <Typography
                                  level="body-xs"
                                  sx={{ color: "text.tertiary" }}
                                >
                                  /sq
                                </Typography>
                              }
                              error={
                                !!(
                                  state.minimum_price_per_square &&
                                  state.price_per_square &&
                                  parseFloat(state.minimum_price_per_square) >
                                    parseFloat(state.price_per_square)
                                )
                              }
                            />
                          </FormControl>
                        </Box>
                        <FormControl size="sm">
                          <FormLabel>Tier</FormLabel>
                          <Select
                            placeholder="None"
                            value={state.tier_type}
                            onChange={(_, value) =>
                              handleTierChange(
                                product.id,
                                value as TierType | null,
                              )
                            }
                          >
                            {tierOptions.map((opt) => (
                              <Option
                                key={opt.value || "none"}
                                value={opt.value}
                              >
                                <Chip
                                  size="sm"
                                  color={opt.color}
                                  variant="soft"
                                >
                                  {opt.label}
                                </Chip>
                              </Option>
                            ))}
                          </Select>
                        </FormControl>
                      </Card>
                    );
                  })}
                </Stack>
              ) : (
                <Sheet
                  variant="outlined"
                  sx={{ borderRadius: "sm", overflow: "hidden" }}
                >
                  <Box sx={{ overflowX: "auto" }}>
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor:
                              "var(--joy-palette-background-level1)",
                          }}
                        >
                          <th
                            style={{
                              padding: "10px 12px",
                              textAlign: "left",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            Product
                          </th>
                          <th
                            style={{
                              padding: "10px 12px",
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 600,
                              width: "140px",
                            }}
                          >
                            Price/Square
                          </th>
                          <th
                            style={{
                              padding: "10px 12px",
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 600,
                              width: "140px",
                            }}
                          >
                            Min Price/Sq
                          </th>
                          <th
                            style={{
                              padding: "10px 12px",
                              textAlign: "center",
                              fontSize: "12px",
                              fontWeight: 600,
                              width: "130px",
                            }}
                          >
                            Tier
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {manufacturer.products.map((product) => {
                          const state = pricingState[product.id] || {
                            price_per_square: "",
                            minimum_price_per_square: "",
                            tier_type: null,
                            existingId: null,
                            hasChanges: false,
                          };
                          return (
                            <tr
                              key={product.id}
                              style={{
                                borderTop:
                                  "1px solid var(--joy-palette-divider)",
                                backgroundColor: state.hasChanges
                                  ? "var(--joy-palette-warning-softBg)"
                                  : "transparent",
                              }}
                            >
                              <td style={{ padding: "8px 12px" }}>
                                <Typography
                                  level="body-sm"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {product.name}
                                </Typography>
                                <Typography
                                  level="body-xs"
                                  textColor="text.tertiary"
                                >
                                  {[
                                    product.sku,
                                    product.warranty_years
                                      ? `${product.warranty_years}-year warranty`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </Typography>
                                {product.description && (
                                  <Typography
                                    level="body-xs"
                                    sx={{ color: "text.tertiary" }}
                                  >
                                    {product.description.length > 80
                                      ? `${product.description.substring(0, 80)}...`
                                      : product.description}
                                  </Typography>
                                )}
                              </td>
                              <td style={{ padding: "8px 12px" }}>
                                <Input
                                  size="sm"
                                  type="number"
                                  placeholder="0.00"
                                  value={state.price_per_square}
                                  onChange={(e) =>
                                    handlePriceChange(
                                      product.id,
                                      e.target.value,
                                    )
                                  }
                                  startDecorator={
                                    <AttachMoney
                                      size={16}
                                      color="var(--joy-palette-text-tertiary)"
                                    />
                                  }
                                  endDecorator={
                                    <Typography
                                      level="body-xs"
                                      sx={{ color: "text.tertiary" }}
                                    >
                                      /sq
                                    </Typography>
                                  }
                                  sx={{
                                    width: "100%",
                                    "--Input-minHeight": "32px",
                                  }}
                                />
                              </td>
                              <td style={{ padding: "8px 12px" }}>
                                <Input
                                  size="sm"
                                  type="number"
                                  placeholder="—"
                                  value={state.minimum_price_per_square}
                                  onChange={(e) =>
                                    handleMinPriceChange(
                                      product.id,
                                      e.target.value,
                                    )
                                  }
                                  startDecorator={
                                    <AttachMoney
                                      size={16}
                                      color="var(--joy-palette-text-tertiary)"
                                    />
                                  }
                                  endDecorator={
                                    <Typography
                                      level="body-xs"
                                      sx={{ color: "text.tertiary" }}
                                    >
                                      /sq
                                    </Typography>
                                  }
                                  error={
                                    !!(
                                      state.minimum_price_per_square &&
                                      state.price_per_square &&
                                      parseFloat(
                                        state.minimum_price_per_square,
                                      ) > parseFloat(state.price_per_square)
                                    )
                                  }
                                  sx={{
                                    width: "100%",
                                    "--Input-minHeight": "32px",
                                  }}
                                />
                              </td>
                              <td style={{ padding: "8px 12px" }}>
                                <Select
                                  size="sm"
                                  placeholder="None"
                                  value={state.tier_type}
                                  onChange={(_, value) =>
                                    handleTierChange(
                                      product.id,
                                      value as TierType | null,
                                    )
                                  }
                                  sx={{
                                    minWidth: 100,
                                    "--Select-minHeight": "32px",
                                  }}
                                >
                                  {tierOptions.map((opt) => (
                                    <Option
                                      key={opt.value || "none"}
                                      value={opt.value}
                                    >
                                      <Chip
                                        size="sm"
                                        color={opt.color}
                                        variant="soft"
                                      >
                                        {opt.label}
                                      </Chip>
                                    </Option>
                                  ))}
                                </Select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                </Sheet>
              )}
            </AccordionDetails>
          </TabbableAccordion>
        ))}
      </AccordionGroup>
      <SettingsSaveBar
        dirty={hasUnsavedChanges}
        saving={saveMutation.isPending}
        label="Save All Changes"
        onSave={() => saveMutation.mutate()}
      />
    </Box>
  );
}
